import { useEffect, useMemo, useState } from 'react';
import { type PaginationData, type UsePaginationDataParams } from '@types';

export function usePaginationData<T> ({
  mode = 'client',
  data,
  totalItems: serverTotalItems = 0,
  onServerRequest,
  initialRowsPerPage = 10,
  initialPage = 1,
}: UsePaginationDataParams<T>): PaginationData<T> {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);

  useEffect(() => {
    if (mode === 'server' && typeof onServerRequest === 'function') {
      onServerRequest(currentPage, rowsPerPage);
    }
  }, [mode, currentPage, rowsPerPage, onServerRequest]);

  useEffect(() => {
    if (mode !== 'client') {
      return;
    }
    const total = data?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, rowsPerPage)));
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [mode, data?.length, rowsPerPage, currentPage]);

  const currentData = useMemo(() => {
    if (mode !== 'client') {
      return [] as T[];
    }
    const items = data ?? [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    return items.slice(startIndex, startIndex + rowsPerPage);
  }, [mode, data, currentPage, rowsPerPage]);

  const totalItems = mode === 'client' ? (data?.length ?? 0) : serverTotalItems;

  return {
    mode,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    currentData,
    totalItems,
  } as const;
}
